// Center-crops a picked file to a square and downscales it to size×size,
// re-encoded as JPEG. createImageBitmap applies EXIF orientation, so phone
// photos come out upright. Throws (via the rejected promise) on files the
// browser can't decode as an image.
export async function fileToSquareJpeg(file: File, size = 512, quality = 0.85): Promise<Blob> {
  // "from-image" postdates this project's TS DOM lib (which only knows
  // flipY/none), hence the cast; browsers accept it
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" } as unknown as ImageBitmapOptions)
  try {
    const side = Math.min(bitmap.width, bitmap.height)
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas not supported")
    ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Image encoding failed"))),
        "image/jpeg",
        quality
      )
    })
  } finally {
    bitmap.close()
  }
}

// PUTs the JPEG to the presigned URL. Both headers are part of the presign
// signature — S3 rejects the upload unless they match it exactly. The
// immutable Cache-Control is safe because every avatar upload gets a fresh
// key, and it spares CloudFront/browser revalidation.
export async function uploadAvatar(uploadUrl: string, blob: Blob): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "image/jpeg",
      "Cache-Control": "public, max-age=31536000, immutable",
    },
    body: blob,
  })
  if (!response.ok) throw new Error(`Upload failed (HTTP ${response.status})`)
}
